<script setup lang="ts">
import {
  groupPositionsByDepartment,
  filterPositions,
  getLocations,
  getDepartments,
} from '@/services/careers'

const ALL_DEPARTMENTS = 'All Departments'
const ALL_LOCATIONS = 'All Locations'

const QUOTES_SECTION = 'QUOTES_SECTION'
const IMAGES_SECTION = 'IMAGES_SECTION'

const ceoImageTransforms = 'f_auto,fl_progressive,w_1500,q_auto'

definePageMeta({
  showNotificationBar: false,
  containsFlushContent: true,
  showFabModal: false,
})

useHead({
  title: 'Careers',
})

const { isMediumUp } = useSkyBreakpoint()
const openRoles = ref(null)

const { positions } = await queryContent('/careers').findOne()

const selectedLocation = ref(ALL_LOCATIONS)
const selectedDepartment = ref(ALL_DEPARTMENTS)
const lifestyleImages = [
  'https://images.grove.co/upload/v1559948046/Product%20Design/careers/lifestyle-1.jpg',
  'https://images.grove.co/upload/v1559948047/Product%20Design/careers/lifestyle-2.jpg',
  'https://images.grove.co/upload/v1559948046/Product%20Design/careers/lifestyle-3.jpg',
].map((url) => transformImage(url, 'f_auto,fl_progressive,w_650,q_auto'))

const ceoImage = computed(() =>
  transformImage(
    'https://images.grove.co/upload/v1560367300/Product%20Design/careers/Jeff-headshot.png',
    ceoImageTransforms
  )
)

const dropdownDepartments = computed(() => {
  const availablePositions = filterPositions(positions, {
    location:
      selectedLocation.value !== ALL_LOCATIONS && selectedLocation.value,
  })
  const availableDepartments = getDepartments(availablePositions)
  const departments = getDepartments(positions, {
    showEmpty: true,
  }).map((department) => ({
    text: department,
    value: department,
    disabled: !availableDepartments.includes(department),
  }))

  departments.unshift({ text: ALL_DEPARTMENTS, value: ALL_DEPARTMENTS })
  return departments
})

const dropdownLocations = computed(() => {
  const availablePositions = filterPositions(positions, {
    department:
      selectedDepartment.value !== ALL_DEPARTMENTS && selectedDepartment.value,
  })
  const availableLocations = getLocations(availablePositions)
  const locations = getLocations(positions).map((location) => ({
    text: location,
    value: location,
    disabled: !availableLocations.includes(location),
  }))

  locations.unshift({ text: ALL_LOCATIONS, value: ALL_LOCATIONS })
  return locations
})

const filteredPositions = computed(() => {
  const options = {}
  if (selectedDepartment.value !== ALL_DEPARTMENTS) {
    options.department = selectedDepartment.value
  }
  if (selectedLocation.value !== ALL_LOCATIONS) {
    options.location = selectedLocation.value
  }
  return filterPositions(positions, options)
})

const isFilterApplied = computed(() => {
  return (
    selectedDepartment.value !== ALL_DEPARTMENTS ||
    selectedLocation.value !== ALL_LOCATIONS
  )
})

const filteredDepartments = computed(() => {
  return getDepartments(filteredPositions.value, {
    showEmpty: !isFilterApplied.value,
  })
})

const departmentSections = computed(() => {
  const result = []

  filteredDepartments.value.forEach((item, index) => {
    result.push(item)
    if (index === 2) {
      result.push(QUOTES_SECTION)
    } else if (index === 5) {
      result.push(IMAGES_SECTION)
    }
  })

  if (!result.includes(QUOTES_SECTION)) {
    result.push(QUOTES_SECTION)
  }

  if (!result.includes(IMAGES_SECTION)) {
    result.push(IMAGES_SECTION)
  }

  return result
})

const positionsByDepartment = computed(() => {
  return groupPositionsByDepartment(filteredPositions.value)
})

function focusRoles() {
  openRoles.value.scrollIntoView()
}

function handleSeeRolesClick() {
  selectedDepartment.value = ALL_DEPARTMENTS
  focusRoles()
}
</script>

<template>
  <NuxtLayout>
    <div class="TheCareersPage">
      <div class="TheCareersPage_TopSection">
        <h1 class="TheCareersPage_TopSection_Header">
          Be proud of the impact you make every day
        </h1>
        <div class="TheCareersPage_TopSection_Copy">
          <p class="TheCareersPage_TopSection_Copy_Paragraph">
            Grove Collaborative is an online brand and retailer for
            <SkyLink inline to="/catalog/?category=grove-collaborative">
              natural home and personal care products.
            </SkyLink>
          </p>
          <p class="TheCareersPage_TopSection_Copy_Paragraph">
            Our replenishment-based service gives families across the country
            access to products they love, that are good for their bodies, their
            homes, and the environment.
          </p>
          <p class="TheCareersPage_TopSection_Copy_Paragraph">
            As a mission-driven B Corp, we believe companies can and should do
            more to improve the world and the communities they serve.
          </p>
          <h2 class="TheCareersPage_TopSection_Subhead">Come join us</h2>
          <div class="TheCareersPage_TopSection_LocationFilters">
            <SkySelect
              id="see-location"
              v-model="selectedLocation"
              :options="dropdownLocations"
              aria-describedby="see-location-instructions"
              block
              label="Location"
            />
            <SkyScreenReader id="see-location-instructions" aria-hidden="true">
              Making a selection from this drop down will change the number of
              available open roles below
            </SkyScreenReader>
            <SkyButton block @click="handleSeeRolesClick"
              >See Open Roles</SkyButton
            >
          </div>
        </div>
      </div>
      <div class="TheCareersPage_GroveSection">
        <h2 class="TheCareersPage_GroveSection_Header">
          Why you’ll love Grove
        </h2>
        <div class="TheCareersPage_GroveSection_Description">
          <div class="TheCareersPage_GroveSection_Description_Column">
            <h3 class="TheCareersPage_GroveSection_Subhead">The Team</h3>
            <p class="TheCareersPage_GroveSection_Description_Column_Paragraph">
              We’re in this together and rely on each other to get things done.
              We work hard because we believe we’ll leave a lasting impact.
            </p>
            <h3 class="TheCareersPage_GroveSection_Subhead">The Benefits</h3>
            <ul class="TheCareersPage_GroveSection_BenefitsList">
              <li>Comprehensive medical, vision and dental</li>
              <li>FSA, Limited FSA and HSA</li>
              <li>Life insurance and disability</li>
              <li>401K</li>
              <li>Free VIP membership and 25% employee discount</li>
            </ul>
          </div>
          <div
            v-if="isMediumUp"
            class="TheCareersPage_GroveSection_Description_Dividers"
          ></div>
          <div class="TheCareersPage_GroveSection_Description_Column">
            <h3 class="TheCareersPage_GroveSection_Subhead">The Mission</h3>
            <p class="TheCareersPage_GroveSection_Description_Column_Paragraph">
              We believe that a small group of people can change the world for
              the better. Grove creates products and funds initiatives that help
              the planet.
            </p>
            <div class="TheCareersPage_GroveSection_Mission">
              <SkyImage
                src="https://images.grove.co/upload/q_auto/c_scale,w_50/v1544489069/global/leaf-illustration.png"
                alt=""
              />
              <p class="TheCareersPage_GroveSection_Mission_Paragraph">
                1 million trees that we will plant in the next 4 years
              </p>
            </div>
            <div class="TheCareersPage_GroveSection_Mission">
              <SkyImage
                src="https://images.grove.co/upload/q_auto/c_scale,w_38/v1544489069/global/spray-bottle-illustration.png"
                alt=""
              />
              <p class="TheCareersPage_GroveSection_Mission_Paragraph">
                600,000 pounds of plastic saved in 2019 from our reusable glass
                vessels
              </p>
            </div>
          </div>
          <div
            v-if="isMediumUp"
            class="TheCareersPage_GroveSection_Description_Dividers"
          ></div>
          <div class="TheCareersPage_GroveSection_Description_Column">
            <h3 class="TheCareersPage_GroveSection_Subhead">The Opportunity</h3>
            <p class="TheCareersPage_GroveSection_Description_Column_Paragraph">
              Our vision is to make consumer products a positive force for human
              and environmental health. And we’re just getting started…
            </p>
            <p
              class="TheCareersPage_GroveSection_Description_Column_Paragraph TheCareersPage_GroveSection_Oportunity"
            >
              We have grown about 10x in the last two years
            </p>
            <p
              class="TheCareersPage_GroveSection_Description_Column_Paragraph TheCareersPage_GroveSection_Oportunity"
            >
              Hundreds of thousands of families across the U.S. receive a
              shipment from Grove every month
            </p>
          </div>
        </div>
      </div>
      <div class="TheCareersPage_CEOSection">
        <div class="TheCareersPage_CEOSection_Image">
          <SkyImage :src="ceoImage" alt="Jeff Yurcisin" />
        </div>
        <div class="TheCareersPage_CEOSection_Quote">
          <h2 class="TheCareersPage_CEOSection_Head">
            “I joined Grove because I fundamentally believe that every
            individual has a responsibility to drive positive change and this
            belief is the common thread that unifies the incredible Grove team.
            We are all committed to a singular purpose that is bigger than
            ourselves and it gives our company a powerful reason to exist.”
          </h2>
          <div class="TheCareersPage_CEOSection_Subhead">
            JEFF YURCISIN, CEO
          </div>
        </div>
      </div>
      <div ref="openRoles" class="TheCareersPage_OpenRoles" tabindex="0">
        <h2 class="TheCareersPage_OpenRoles_Header">
          Open Roles ({{ filteredPositions.length }})
        </h2>
        <div class="TheCareersPage_OpenRoles_Filters">
          <SkySelect
            id="location"
            v-model="selectedLocation"
            :options="dropdownLocations"
            aria-describedby="see-location-instructions"
            label="Location"
          />
          <SkySelect
            id="department"
            v-model="selectedDepartment"
            :options="dropdownDepartments"
            item-disabled="disabled"
            item-text="department"
            item-value="department"
            label="Department"
          />
        </div>
      </div>
      <template v-for="department in departmentSections">
        <div
          v-if="department === QUOTES_SECTION"
          :key="`department-carousel-${department}`"
          class="TheCareersPage_QuotesSection"
        >
          <CareersQuoteCarousel class="TheCareersPage_QuotesSection_Carousel" />
        </div>
        <div
          v-else-if="department === IMAGES_SECTION"
          :key="`department-images-${department}`"
          class="TheCareersPage_ImagesSection"
        >
          <SkyImage
            v-if="isMediumUp"
            class="TheCareersPage_ImagesSection_Container"
            src="https://images.grove.co/upload/f_auto,fl_progressive,w_1000,q_auto/v1557194833/Product%20Design/careers/lifestyle-gallery.jpg"
            alt=""
          />
          <SkyCarousel
            v-else
            class="TheCareersPage_ImagesSection_Container"
            draggable
            pagination
          >
            <div
              v-for="(image, imageIndex) in lifestyleImages"
              :key="`careers-lifestyle-slide-${imageIndex}`"
            >
              <SkyImage :src="image" alt="" />
            </div>
          </SkyCarousel>
        </div>
        <CareersDepartment
          v-else
          :key="`department-${department}`"
          :department-name="department"
          :positions="positionsByDepartment[department]"
          :is-filter-applied="isFilterApplied"
          class="TheCareersPage_DepartmentSection"
          @scroll-top="focusRoles"
        />
      </template>
      <div class="TheCareersPage_ContactUs">
        <h3 class="TheCareersPage_ContactUs_Header">We’re always growing!</h3>
        <p>
          Send us your resume and we’ll contact you as relevant future
          opportunities open up.
        </p>
        <SkyLink display to="mailto:talent@grove.co"> Contact Us </SkyLink>
      </div>
    </div>
  </NuxtLayout>
</template>

<style lang="scss">
.TheCareersPage {
  --dropdown-careers-max-width: 480px;
  &_TopSection {
    background: url(https://images.grove.co/upload/v1555509528/careers-top-bg_ta6c1x.svg)
      no-repeat center;
    background-size: cover;
    padding: var(--spacing-12x) 0px;
    text-align: center;
    @include for-medium-up {
      padding: 80px 0px;
    }

    &_Header {
      @include type-brand-display('1');
      margin: var(--spacing-4x) 0px var(--spacing-8x);
      padding: 0px var(--spacing-2x);
    }

    &_Copy {
      max-width: 1000px;
      margin: 0px auto;
      padding: 0px var(--spacing-2x);
      @include for-medium-up {
        padding: 0px var(--spacing-16x);
      }

      &_Paragraph {
        @include type-body(l);
        margin-bottom: var(--spacing-8x);
      }
    }

    &_Subhead {
      @include type-heading('2');
      margin-bottom: var(--spacing-4x);
    }

    &_LocationFilters {
      display: grid;
      gap: var(--spacing-2x);
      margin: 0px var(--spacing-2x);
      @include for-medium-up {
        margin: 0px auto;
        max-width: var(--dropdown-careers-max-width);
        grid-template-columns: 1fr auto;
      }
    }
  }

  &_GroveSection {
    background-color: var(--surface-color-linen);
    padding: var(--spacing-12x) 0px;
    text-align: center;

    &_Header {
      @include type-brand-display('2');
      margin: var(--spacing-4x) 0px var(--spacing-12x);
    }

    &_Subhead {
      @include type-heading('3');
      margin-bottom: var(--spacing-2x);
    }

    &_BenefitsList {
      list-style-type: none;
      margin: 0px 0px var(--spacing-8x);
      padding-inline-start: 0;
    }

    &_Oportunity {
      @include for-medium-up {
        max-width: 192px;
        margin-left: auto;
        margin-right: auto;
      }
    }

    &_Mission {
      align-items: center;
      display: grid;
      grid-template-columns: var(--spacing-24x) auto;
      justify-items: center;
      margin-bottom: var(--spacing-8x);

      &_Paragraph {
        margin: 0px;
      }
    }

    &_Description {
      @include type-body(m);
      display: grid;
      grid-template-columns: auto;
      margin: 0px auto;
      max-width: 1280px;
      @include for-medium-up {
        grid-template-columns: 1fr 1px 1fr 1px 1fr;
      }

      &_Dividers {
        display: block;
        width: 1px;
        height: 375px;
        background-color: #e6e5e0;
      }

      &_Column {
        padding: 0px var(--spacing-4x);
        @include for-medium-up {
          padding: 0px var(--spacing-12x);
        }

        &_Paragraph {
          margin-bottom: var(--spacing-8x);
        }
      }
    }
  }

  &_CEOSection {
    align-items: center;
    background-color: var(--surface-color-frost);
    display: grid;
    grid-template-columns: auto;
    @include for-medium-up {
      grid-template-columns: 1fr 1fr;
    }

    &_Image {
      background-color: var(--surface-color-frost);
      height: 100%;
    }

    &_Quote {
      padding: var(--spacing-8x);
      @include for-medium-up {
        padding: var(--spacing-8x) var(--spacing-24x);
      }
    }

    &_Head {
      @include type-brand-display('2');
      margin-bottom: var(--spacing-8x);
      @include for-medium-up {
        font-size: var(--font-size-400);
      }
    }

    &_Subhead {
      @include type-body(xs);
      letter-spacing: var(--letter-spacing-loose);
    }
  }

  &_OpenRoles {
    padding: var(--spacing-12x) 0px;
    text-align: center;

    &_Header {
      @include type-brand-display('2');
      font-family: var(--font-family-default-medium);
      margin-bottom: var(--spacing-4x);
    }

    &_Filters {
      display: grid;
      gap: var(--spacing-2x);
      margin: 0px var(--spacing-2x);
      @include for-medium-up {
        margin: 0px auto;
        max-width: var(--dropdown-careers-max-width);
        grid-template-columns: repeat(2, 1fr);
      }
    }
  }

  &_QuotesSection {
    background-color: var(--surface-color-linen);
    padding: var(--spacing-12x) 0px;

    &_Carousel {
      max-width: 1000px;
      margin: 0px auto;
    }
  }

  &_ImagesSection {
    background-color: var(--surface-color-frost);
    padding: var(--spacing-12x) 0px;
    text-align: center;

    &_Container {
      max-width: 1000px;
      padding: 0px var(--spacing-2x);
    }
  }

  &_DepartmentSection {
    border-top: 1px solid var(--border-color-default);
    padding: var(--spacing-6x) 0;
  }

  &_ContactUs {
    max-width: 1000px;
    margin: 0px auto;
    padding: var(--spacing-16x) var(--spacing-2x);

    &_Header {
      @include type-heading('3');
      margin-bottom: var(--spacing-2x);
      @include for-medium-up {
        @include type-heading('1');
      }
    }
  }
}
</style>
